import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { SearchBar, Pagination, FilterSelect } from './ListControls';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import { Loader2, RefreshCw, Download, Users, UserPlus } from 'lucide-react';
import { ExportListButtons } from './ExportListButtons';
import { toast } from 'sonner';
import { UserActionsDropdown } from './admin/UserActionsDropdown';
import {
  UserEditDialog, UserBanDialog, UserDeleteDialog,
  UserPasswordDialog, UserWalletAdjustDialog
} from './admin/UserActionDialogs';
import { UserAddDialog } from './admin/UserAddDialog';
import { UserDocsDialog } from './admin/UserDocsDialog';
import { UserDownlinesDialog } from './admin/UserDownlinesDialog';

export function AdminUsersPage() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [limit, setLimit] = useState(15);
  const [roleFilter, setRoleFilter] = useState('');
  const [bannedFilter, setBannedFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [availablePlans, setAvailablePlans] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [editForm, setEditForm] = useState({});
  const [editPriorDeletions, setEditPriorDeletions] = useState([]);
  const [banForm, setBanForm] = useState({ reason: '', notes: '' });
  const [deleteForm, setDeleteForm] = useState({ reason: '' });
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm: '' });
  const [walletForm, setWalletForm] = useState({ amount: '', note: '', passkey: '', currentBalance: null });
  const [addOpen, setAddOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [downlinesOpen, setDownlinesOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      let url = `/api/admin/users?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (roleFilter) url += `&role=${roleFilter}`;
      if (planFilter) url += `&subscription_plan=${encodeURIComponent(planFilter)}`;
      if (bannedFilter) url += `&include_banned=${bannedFilter}`;
      const { data } = await api.get(url);
      setUsers(data.users);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [api, skip, limit, search, statusFilter, roleFilter, planFilter, bannedFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setSkip(0); }, [search, statusFilter, roleFilter, planFilter, limit, bannedFilter]);

  // Load role-based counts for the header (independent of the table filter).
  const fetchCounts = useCallback(() => {
    api.get('/api/admin/users/counts')
      .then(({ data }) => setCounts(data))
      .catch(() => setCounts(null));
  }, [api]);
  useEffect(() => { fetchCounts(); }, [fetchCounts, users.length]);

  // Load active subscription plans once for the Tier filter dropdown.
  useEffect(() => {
    api.get('/api/admin/subscription-plans?limit=100')
      .then(({ data }) => setAvailablePlans((data?.plans || []).filter((p) => p.is_active !== false)))
      .catch(() => {});
  }, [api]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });

  const toggleActions = (id) => setActionsOpen(actionsOpen === id ? null : id);

  const openEdit = async (user) => {
    setSelectedUser(user);
    let priorDeletions = [];
    try {
      const { data } = await api.get(`/api/admin/users/${user.id}`);
      setEditForm({
        name: data.user.name || '',
        email: data.user.email || '',
        mobile: data.user.mobile || '',
        role: data.user.role || 'member',
        subscription_status: data.user.subscription_status || 'inactive',
        subscription_plan: data.user.subscription_plan || '',
      });
      priorDeletions = data.user.prior_deletions || [];
    } catch {
      setEditForm({
        name: user.name || '', email: user.email || '', mobile: user.mobile || '',
        role: user.role || 'member', subscription_status: user.subscription_status || 'inactive',
        subscription_plan: user.subscription_plan || '',
      });
    }
    setEditPriorDeletions(priorDeletions);
    setActionsOpen(null);
    setEditOpen(true);
  };

  const openBan = (user) => {
    setSelectedUser(user); setBanForm({ reason: '', notes: '' }); setActionsOpen(null); setBanOpen(true);
  };
  const openDelete = (user) => {
    setSelectedUser(user); setDeleteForm({ reason: '' }); setActionsOpen(null); setDeleteOpen(true);
  };
  const openPassword = (user) => {
    setSelectedUser(user); setPasswordForm({ new_password: '', confirm: '' }); setActionsOpen(null); setPasswordOpen(true);
  };
  const openWallet = async (user) => {
    setSelectedUser(user);
    setWalletForm({ amount: '', note: '', passkey: '', currentBalance: null });
    setActionsOpen(null);
    setWalletOpen(true);
    try {
      const { data } = await api.get(`/api/admin/users/${user.id}`);
      const bal = data.wallet?.current_value ?? 0;
      setWalletForm(prev => ({ ...prev, currentBalance: bal }));
    } catch (e) {
      console.error('Failed to fetch wallet balance');
    }
  };

  const handleEdit = async () => {
    setActionLoading(true);
    try {
      await api.put(`/api/admin/users/${selectedUser.id}`, editForm);
      toast.success('User updated');
      setEditOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setActionLoading(false);
  };

  const handleBan = async () => {
    if (!banForm.reason) { toast.error('Select a ban reason'); return; }
    setActionLoading(true);
    try {
      const { data } = await api.post(`/api/admin/users/${selectedUser.id}/ban`, banForm);
      toast.success(data.message);
      setBanOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ban failed'); }
    setActionLoading(false);
  };

  const handleUnban = async (user) => {
    setActionsOpen(null);
    try {
      await api.post(`/api/admin/users/${user.id}/unban`);
      toast.success('User unbanned');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Unban failed'); }
  };

  const handleDelete = async () => {
    if (!deleteForm.reason) { toast.error('Select a deletion reason'); return; }
    setActionLoading(true);
    try {
      const { data } = await api.delete(`/api/admin/users/${selectedUser.id}`, { data: deleteForm });
      let msg = data.message || 'User deleted';
      if (data.merchant_services_deactivated > 0) {
        msg += ` · ${data.merchant_services_deactivated} listings deactivated`;
      }
      if (data.mro_recruits_unlinked > 0) {
        msg += ` · ${data.mro_recruits_unlinked} MRO recruits unlinked`;
      }
      toast.success(msg);
      setDeleteOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    setActionLoading(false);
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password.length < 6) { toast.error('Min 6 characters'); return; }
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error('Passwords do not match'); return; }
    setActionLoading(true);
    try {
      await api.post(`/api/admin/users/${selectedUser.id}/change-password`, { new_password: passwordForm.new_password });
      toast.success('Password changed');
      setPasswordOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleWalletAdjust = async () => {
    const amt = parseFloat(walletForm.amount);
    if (!amt || isNaN(amt)) { toast.error('Enter a valid amount'); return; }
    if (!walletForm.passkey) { toast.error('Passkey is required'); return; }
    setActionLoading(true);
    try {
      const { data } = await api.post(`/api/admin/users/${selectedUser.id}/adjust-wallet`, {
        amount: amt, note: walletForm.note || 'Admin adjustment', passkey: walletForm.passkey
      });
      toast.success(data.message);
      setWalletOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Adjustment failed'); }
    setActionLoading(false);
  };

  const exportCsv = async () => {
    try {
      let url = `/api/reports/users/export?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'users_export.csv'; a.click();
      toast.success('Users exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">User Management</h1>
            {counts ? (
              <p className="text-[#A0B4CC] mt-1 text-sm" data-testid="user-counts-summary">
                <span className="text-white font-semibold">{counts.total}</span> total &nbsp;·&nbsp;
                <span className="text-[#00E5FF] font-semibold" data-testid="count-members-total">{counts.members?.total || 0}</span> members
                <span className="text-[#5C7599] text-xs"> ({counts.members?.paid || 0} paid · {counts.members?.free || 0} free)</span>
                {' · '}
                <span className="text-[#00E676] font-semibold" data-testid="count-merchants-total">{counts.merchants?.total || 0}</span> merchants
                <span className="text-[#5C7599] text-xs"> ({counts.merchants?.approved || 0} approved · {counts.merchants?.pending || 0} pending)</span>
                {counts.mros > 0 && (
                  <> · <span className="text-[#FFEA00]">{counts.mros}</span> MROs</>
                )}
                {(counts.moderators + counts.cros + counts.admins) > 0 && (
                  <> · <span className="text-[#5C7599]">{counts.moderators + counts.cros + counts.admins} staff</span></>
                )}
              </p>
            ) : (
              <p className="text-[#A0B4CC] mt-1">Total {total}</p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setAddOpen(true)} className="btn-primary" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Add User
            </Button>
            <ExportListButtons api={api} resource="users" filename="users" />
            <Button onClick={fetchUsers} variant="ghost" className="text-[#00E5FF]" data-testid="refresh-users-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex-1 min-w-[200px]">
            <SearchBar value={search} onChange={setSearch} placeholder="Search by name, email or code..." />
          </div>
          <FilterSelect
            value={statusFilter} onChange={setStatusFilter} placeholder="All Statuses"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'expired', label: 'Expired' },
              { value: 'pending', label: 'Pending' },
            ]}
          />
          <FilterSelect
            value={roleFilter} onChange={setRoleFilter} placeholder="All Roles"
            options={[
              { value: 'member', label: 'Member' },
              { value: 'merchant', label: 'Merchant' },
              { value: 'mro', label: 'MRO' },
              { value: 'moderator', label: 'Moderator' },
              { value: 'cro', label: 'CRO' },
              { value: 'admin', label: 'Admin' },
            ]}
            data-testid="filter-role"
          />
          <FilterSelect
            value={planFilter}
            onChange={setPlanFilter}
            placeholder="All Tiers"
            options={availablePlans.map((p) => ({ value: p.name, label: p.name }))}
            data-testid="filter-tier"
          />
          <FilterSelect
            value={bannedFilter} onChange={setBannedFilter} placeholder="All Users"
            options={[{ value: 'banned_only', label: 'Banned Only' }]}
          />
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" />
              </div>
            ) : users.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-12 h-12 text-[#5C7599] mx-auto mb-3 opacity-60" />
                <p className="text-[#A0B4CC] font-medium">No users found</p>
                <p className="text-[#5C7599] text-sm mt-1">
                  {(search || statusFilter || roleFilter || bannedFilter) ? 'Try adjusting your filters' : 'Members will appear here as they register'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#A0B4CC]">Name</TableHead>
                      <TableHead className="text-[#A0B4CC]">Email</TableHead>
                      <TableHead className="text-[#A0B4CC]">Role</TableHead>
                      <TableHead className="text-[#A0B4CC]">Status</TableHead>
                      <TableHead className="text-[#A0B4CC]">Referral Code</TableHead>
                      <TableHead className="text-[#A0B4CC]">Joined</TableHead>
                      <TableHead className="text-[#A0B4CC] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} className={`border-white/5 hover:bg-white/5 ${user.is_banned ? 'opacity-60' : ''}`}>
                        <TableCell className="text-white font-medium">
                          <div className="flex items-center gap-2">
                            {user.name}
                            {user.prior_deletions_count > 0 && (
                              <span
                                className="inline-block w-2 h-2 rounded-full bg-[#FF1744] flex-shrink-0"
                                title={`Re-registered account · ${user.prior_deletions_count} prior deletion${user.prior_deletions_count > 1 ? 's' : ''}`}
                                data-testid={`prior-deletion-indicator-${user.id}`}
                              />
                            )}
                            {user.is_banned && <Badge className="bg-[#FF1744]/20 text-[#FF1744] text-[10px]">Banned</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-[#A0B4CC]">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={user.role === 'admin' ? 'bg-[#007AFF]/20 text-[#007AFF]' : 'bg-[#00E5FF]/20 text-[#00E5FF]'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge className={user.subscription_status === 'active' ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#5C7599]/20 text-[#5C7599]'}>
                              {user.subscription_status}
                            </Badge>
                            {user.subscription_admin_activated && (
                              <span
                                title="Admin-activated — bonuses (referrals, stock cert) are NOT credited for this account"
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-amber-400/10 text-amber-400 border border-amber-400/30 whitespace-nowrap"
                                data-testid={`user-admin-activated-tag-${user.id}`}
                              >
                                Silent · no bonuses
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-[#00E5FF]">{user.referral_code}</TableCell>
                        <TableCell className="text-[#5C7599]">{formatDate(user.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <UserActionsDropdown
                            user={user}
                            isOpen={actionsOpen === user.id}
                            onToggle={toggleActions}
                            onEdit={openEdit}
                            onPassword={openPassword}
                            onWallet={openWallet}
                            onBan={openBan}
                            onUnban={handleUnban}
                            onDelete={openDelete}
                            onViewDocs={(u) => { setSelectedUser(u); setDocsOpen(true); setActionsOpen(null); }}
                            onViewDownlines={(u) => { setSelectedUser(u); setDownlinesOpen(true); setActionsOpen(null); }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Pagination skip={skip} limit={limit} total={total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />

        {actionsOpen && <div className="fixed inset-0 z-40" onClick={() => setActionsOpen(null)} />}

        <UserEditDialog open={editOpen} onOpenChange={setEditOpen} user={selectedUser} form={editForm} setForm={setEditForm} priorDeletions={editPriorDeletions} onSave={handleEdit} loading={actionLoading} />
        <UserBanDialog open={banOpen} onOpenChange={setBanOpen} user={selectedUser} form={banForm} setForm={setBanForm} onConfirm={handleBan} loading={actionLoading} />
        <UserDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} user={selectedUser} form={deleteForm} setForm={setDeleteForm} onConfirm={handleDelete} loading={actionLoading} />
        <UserPasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} user={selectedUser} form={passwordForm} setForm={setPasswordForm} onSave={handlePasswordChange} loading={actionLoading} />
        <UserWalletAdjustDialog open={walletOpen} onOpenChange={setWalletOpen} user={selectedUser} form={walletForm} setForm={setWalletForm} onConfirm={handleWalletAdjust} loading={actionLoading} />
        <UserAddDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          api={api}
          onCreated={(data, password) => {
            toast.success(`User created · Temp password: ${password}`, { duration: 10000 });
            setAddOpen(false);
            fetchUsers();
          }}
        />
        <UserDocsDialog open={docsOpen} onOpenChange={setDocsOpen} user={selectedUser} />
        <UserDownlinesDialog open={downlinesOpen} onOpenChange={setDownlinesOpen} user={selectedUser} api={api} />
      </div>
    </AdminSidebarLayout>
  );
}
